import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Pagination, Response } from '../../../_shared/models';
import { FuelKpiRequest } from '../../_shared';
import { FuelKpiSummaryResponse } from '../../_shared/models/fuel-kpi-response.model';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { KpiFactorResponse } from '../../_shared/models/fuel-kpi-factor-response.model';
import { AssetFuelChangesResponse, AssetFuelLevelResponse, AssetFuelLevelSummaryResponse, AutoValidatedDecreaseResponse, AutoValidationRequest, FuelChangesDetailsResponse, FuelChangesLocationsResponse, FuelChangesRequest, FuelDecreaseInvestigationRequest, FuelDecreaseInvestigationResponse } from '../models/fuel-changes-details.model';
import { PaginationRequest } from '../../../_shared/models/pagination-request.model';

@Injectable({
  providedIn: 'root',
})
export class FuelChangesService {
  constructor(private http: HttpClient) { }

  async getFuelLossKpiStats(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiSummaryResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/total-fuel-loss-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelLossKpiFactors(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<KpiFactorResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/total-fuel-loss-factors`, request).pipe(takeUntil(cancellation)));
  }

  async getTotalFillUpsKpiStats(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiSummaryResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/total-fill-ups-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getTotalFillUpsKpiFactors(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<KpiFactorResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/total-fill-ups-factors`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelLossRatioKpiStats(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiSummaryResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-loss-ratio-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelLossRatioKpiFactors(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<KpiFactorResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-loss-ratio-factors`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelConsumptionKpiStats(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiSummaryResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-consumption-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelConsumptionKpiFactors(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<KpiFactorResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-consumption-factors`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelChangesDetails(request: PaginationRequest, cancellation: Subject<void>): Promise<Response<Pagination<FuelChangesDetailsResponse[]>>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-changes-details`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelChangesLocations(request: FuelChangesRequest, cancellation: Subject<void>): Promise<Response<FuelChangesLocationsResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-changes-locations`, request).pipe(takeUntil(cancellation)));
  }

  async getCurrentFuelLevel(assetId: number, cancellation: Subject<void>): Promise<Response<AssetFuelLevelResponse>> {
    return firstValueFrom(this.http.get(`${environment.apiEndpoint}/fuelchanges/current-fuel-level/${assetId}`).pipe(takeUntil(cancellation)));
  }

  async getFuelChanges(assetId: number, decreaseTimeSent: Date, cancellation: Subject<void>): Promise<Response<AssetFuelChangesResponse[]>> {
    let params = new HttpParams()
      .set('assetId', assetId.toString())
      .set('decreaseTimeSent', decreaseTimeSent.toString());
    return firstValueFrom(this.http.get(`${environment.apiEndpoint}/fuelchanges/fuel-changes`, { params: params }).pipe(takeUntil(cancellation)));
  }

  async getAssetFuelChangesSummary(assetId: number, decreaseTimeSent: Date, cancellation: Subject<void>): Promise<Response<AssetFuelLevelSummaryResponse>> {
    let params = new HttpParams()
      .set('assetId', assetId.toString())
      .set('decreaseTimeSent', decreaseTimeSent.toString());
    return firstValueFrom(this.http.get(`${environment.apiEndpoint}/fuelchanges/asset-fuel-summary`, { params: params }).pipe(takeUntil(cancellation)));
  }

  async getFuelDecreaseInvestigation(assetId: number, fuelCostDetailId: number, cancellation: Subject<void>): Promise<Response<FuelDecreaseInvestigationResponse>> {
    let params = new HttpParams()
      .set('assetId', assetId.toString())
      .set('fuelCostDetailId', fuelCostDetailId.toString());
    return firstValueFrom(this.http.get(`${environment.apiEndpoint}/fuelchanges/investigation`, { params: params }).pipe(takeUntil(cancellation)));
  }

  async upsertFuelDecreaseInvestigation(request: FuelDecreaseInvestigationRequest, cancellation: Subject<void>): Promise<Response<string>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/investigation`, request).pipe(takeUntil(cancellation)));
  }

  async AutoValidateDecrease(request: AutoValidationRequest, cancellation: Subject<void>): Promise<Response<AutoValidatedDecreaseResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelchanges/auto-validate`, request).pipe(takeUntil(cancellation)));
  }

  async exportFuelChangesDetailsExcel(request: FuelChangesRequest): Promise<Blob> {
    return firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-changes-details/export/xlsx`, request, {
        responseType: 'blob',
        observe: 'body'
      })
    );
  }

  async exportFuelChangesDetailsCSV(request: FuelChangesRequest): Promise<Blob> {
    return firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/fuelchanges/fuel-changes-details/export/csv`, request, {
        responseType: 'blob',
        observe: 'body'
      })
    );
  }
}